import React, { useState, useEffect, useMemo } from 'react';
import { distanceBetween } from 'geofire-common';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import withVisionAura from './visions/withVisionAura';
import useOnScreen from '../hooks/useOnScreen';
import FilterModal from './FilterModal';

const ProfilePicture = withVisionAura(({ src, alt, onClick, className, userId }) => (
    <img src={src} alt={alt} onClick={onClick} className={className} />
));

const MemoizedUserCard = React.memo(({ profile, onUserSelect, onVisible, distanceUnit }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.1 });

    useEffect(() => {
        if (isVisible) {
            onVisible();
        }
    }, [isVisible, onVisible]);

    return (
        <div ref={ref}>
            <UserCard profile={profile} onUserSelect={onUserSelect} distanceUnit={distanceUnit} />
        </div>
    );
});

const UserCard = ({ profile, onUserSelect, distanceUnit }) => {
    const { t } = useTranslation();
    const { onlineStatus, setMediaToView } = useAppContext();
    const status = onlineStatus[profile.id];
    const isOnline = status?.isOnline;
    const themeClass = profile?.profileTheme ? `user-card-theme-${profile.profileTheme}` : 'user-card-theme-default';

    const displayDistance = distanceUnit === 'km' ? profile.distance * 1.60934 : profile.distance;

    return (
        <div
            onClick={() => onUserSelect(profile.id)}
            className={`bg-gray-800/70 p-4 rounded-lg flex flex-col items-center text-center transition-all duration-300 hover:bg-gray-700/90 hover:scale-105 cursor-pointer h-full ${themeClass}`}
        >
            <div className="relative">
                <ProfilePicture
                    onClick={(e) => { e.stopPropagation(); setMediaToView(profile.photoURL); }}
                    src={profile.photoURL || "https://placehold.co/100x100/AEC6CF/FFFFFF?text=U"}
                    alt={profile.displayName}
                    className="w-24 h-24 rounded-full object-cover border-4 border-gray-600"
                    userId={profile.id}
                />
                {isOnline && <span className="absolute bottom-1 right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-gray-800" title={t('usersList.onlineStatus')}></span>}
                {profile.isAI && <span className="absolute top-1 right-1 p-1 bg-purple-500 rounded-full" title={t('usersList.aiPersonaTitle')}><LucideIcons.BrainCircuit size={12} className="text-white" /></span>}
            </div>
            <div className="w-full mt-3">
                <h4 className="font-bold text-white truncate w-full">{profile.displayName}</h4>
                <p className="text-xs text-gray-400 truncate w-full">{profile.interests?.join(', ') || t('usersList.defaultInterests')}</p>
                {profile.distance !== null && (
                    <p className="text-xs text-gray-500 mt-1">
                        <LucideIcons.MapPin size={12} className="inline mr-1" />
                        {displayDistance.toFixed(1)} {distanceUnit} away
                    </p>
                )}
            </div>
        </div>
    );
};

function UsersList({ onUserSelect }) {
    const { t } = useTranslation();
    const { db, query, collection, orderBy, getDocs, onlineStatus, currentUserProfile, setMessage, appId } = useAppContext();
    
    const [activeTab, setActiveTab] = useState('all');
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [searchRadius, setSearchRadius] = useState(25);
    const [isRadiusFilterEnabled, setIsRadiusFilterEnabled] = useState(false);
    const [distanceUnit, setDistanceUnit] = useState('miles');

    const USERS_PER_PAGE = 20;

    useEffect(() => {
        const fetchMasterUserList = async () => {
            setIsLoading(true);
            try {
                const usersCollection = collection(db, `artifacts/${appId}/public/data/user_profiles`);
                const allUsersSnapshot = await getDocs(query(usersCollection));
                const allFirestoreUsers = allUsersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllUsers(allFirestoreUsers);
            } catch (error) {
                console.error("Error fetching master user list:", error);
                setMessage(t('usersList.fetchError', { message: error.message }));
            } finally {
                setIsLoading(false);
            }
        };
        fetchMasterUserList();
    }, [db, appId, setMessage, t]);

    const filteredAndSortedUsers = useMemo(() => {
        let processedUsers = [...allUsers];

        if (activeTab === 'ai') {
            processedUsers = processedUsers.filter(user => user.isAI);
        } else if (activeTab === 'online') {
            const onlineUserIds = Object.keys(onlineStatus).filter(uid => onlineStatus[uid]?.isOnline === true);
            processedUsers = processedUsers.filter(user => !user.isAI && onlineUserIds.includes(user.id));
        }

        if (searchTerm) {
            processedUsers = processedUsers.filter(user =>
                user.displayName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        let usersWithDistance = processedUsers.map(user => {
            const currentUserLat = currentUserProfile?.location?.latitude;
            const currentUserLng = currentUserProfile?.location?.longitude;
            const userLat = user.location?.latitude;
            const userLng = user.location?.longitude;

            if (typeof currentUserLat === 'number' && typeof currentUserLng === 'number' &&
                typeof userLat === 'number' && typeof userLng === 'number') {
                const distanceInKm = distanceBetween([userLat, userLng], [currentUserLat, currentUserLng]);
                return { ...user, distance: distanceInKm * 0.621371 };
            }
            return { ...user, distance: null };
        });

        if (isRadiusFilterEnabled) {
            usersWithDistance = usersWithDistance.filter(user => user.distance !== null && user.distance <= searchRadius);
        }

        usersWithDistance.sort((a, b) => {
            if (activeTab === 'new') {
                const aDate = a.createdAt?.toDate() || new Date(0);
                const bDate = b.createdAt?.toDate() || new Date(0);
                if (aDate < bDate) return 1;
                if (aDate > bDate) return -1;
            }

            const aHasDistance = a.distance !== null;
            const bHasDistance = b.distance !== null;
            if (aHasDistance && bHasDistance) return a.distance - b.distance;
            if (aHasDistance) return -1;
            if (bHasDistance) return 1;
            return (a.displayName || '').localeCompare(b.displayName || '');
        });

        return usersWithDistance;
    }, [allUsers, searchTerm, activeTab, onlineStatus, currentUserProfile, isRadiusFilterEnabled, searchRadius]);

    useEffect(() => {
        const firstPage = filteredAndSortedUsers.slice(0, USERS_PER_PAGE);
        setUsers(firstPage);
        setHasMore(firstPage.length < filteredAndSortedUsers.length);
    }, [filteredAndSortedUsers]);

    const loadMoreUsers = () => {
        if (isLoading || !hasMore) return;
        
        const startIndex = users.length;
        const endIndex = startIndex + USERS_PER_PAGE;
        const newUsersChunk = filteredAndSortedUsers.slice(startIndex, endIndex);

        setUsers(prev => [...prev, ...newUsersChunk]);
        setHasMore(endIndex < filteredAndSortedUsers.length);
    };

    const renderList = () => {
        if (isLoading && users.length === 0) {
            return <LoadingSpinner message={t('usersList.loadingMessage')} />;
        }
        if (users.length === 0) {
            const emptyMessage = activeTab === 'online' ? t('usersList.emptyOnline', 'No users are currently online.') : t('usersList.emptyAll');
            return <p className="text-center text-gray-400 italic mt-8">{emptyMessage}</p>;
        }

        return (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {users.map((user, index) => {
                    const isLastElement = index === users.length - 1;
                    return (
                        <MemoizedUserCard
                            key={user.id}
                            profile={user}
                            onUserSelect={onUserSelect}
                            distanceUnit={distanceUnit}
                            onVisible={() => {
                                if (isLastElement && hasMore && !isLoading) {
                                    loadMoreUsers();
                                }
                            }}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className="p-4 sm:p-6 bg-black bg-opacity-50 rounded-lg shadow-lg max-w-4xl mx-auto text-white">
            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                searchRadius={searchRadius}
                setSearchRadius={setSearchRadius}
                isRadiusFilterEnabled={isRadiusFilterEnabled}
                setIsRadiusFilterEnabled={setIsRadiusFilterEnabled}
                distanceUnit={distanceUnit}
                setDistanceUnit={setDistanceUnit}
            />

            <h2 className="text-3xl font-bold text-center mb-6 text-blue-300 font-playfair">{t('usersList.title')}</h2>

            <div className="flex justify-center border-b border-gray-700 mb-6">
                <button onClick={() => setActiveTab('online')} className={`px-4 py-2 text-sm font-semibold ${activeTab === 'online' ? 'text-white border-b-2 border-green-400' : 'text-gray-400'}`}>{t('usersList.tabs.online')}</button>
                <button onClick={() => setActiveTab('all')} className={`px-4 py-2 text-sm font-semibold ${activeTab === 'all' ? 'text-white border-b-2 border-blue-400' : 'text-gray-400'}`}>{t('usersList.tabs.all')}</button>
                <button onClick={() => setActiveTab('new')} className={`px-4 py-2 text-sm font-semibold ${activeTab === 'new' ? 'text-white border-b-2 border-yellow-400' : 'text-gray-400'}`}>{t('usersList.tabs.new')}</button>
                <button onClick={() => setActiveTab('ai')} className={`px-4 py-2 text-sm font-semibold ${activeTab === 'ai' ? 'text-white border-b-2 border-purple-400' : 'text-gray-400'}`}>{t('usersList.tabs.ai')}</button>
            </div>

            <div className="mb-4 flex justify-center items-center gap-2">
                <input
                    type="text"
                    placeholder={t('usersList.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-gray-700 text-white p-2 rounded-md w-full max-w-md"
                />
                <button onClick={() => setIsFilterModalOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-md">
                    <LucideIcons.Filter size={20} />
                </button>
            </div>

            {renderList()}

            {isLoading && users.length > 0 && (
                <div className="text-center mt-4">
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
}

export default UsersList;
